import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { HelpCircle, LifeBuoy, Loader2, Send, Ticket, Phone, ImagePlus, Camera, X, Plus } from "lucide-react";

const CATEGORIES = ["Delivery", "Product", "Payment", "Other"];
const PRIORITIES = [
  { key: "low",    label: "Low",    color: "text-slate-600 bg-slate-100" },
  { key: "medium", label: "Normal", color: "text-blue-700 bg-blue-50" },
  { key: "high",   label: "Urgent", color: "text-red-700 bg-red-50" },
];

const STATUS_META = {
  pending:     { label: "New",         color: "bg-slate-100 text-slate-700" },
  in_progress: { label: "In progress", color: "bg-blue-50 text-blue-700" },
  on_hold:     { label: "On hold",     color: "bg-amber-50 text-amber-800" },
  done:        { label: "Resolved",    color: "bg-emerald-50 text-emerald-700" },
};

const MAX_COMPLAINTS = 5;
const MAX_PHOTOS_PER_COMPLAINT = 3;
const emptyComplaint = () => ({ key: Math.random().toString(36).slice(2), description: "", attachments: [] });

export function TicketDialog({ open, onOpenChange, orders = [], loginMobile = "", onCreated }) {
  const [form, setForm] = useState({
    subject: "",
    category: "Other",
    priority: "medium",
    order_id: "",
    contact_mobile: "",
    // Up to 5 complaints per ticket — each has its own description + up to
    // 3 photos. The first row is always present (primary complaint); the
    // customer can tap "+ Add another complaint" to add 4 more.
    complaints: [emptyComplaint()],
  });
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill the contact number with the logged-in mobile so a returning
  // customer can submit with one tap. They can still override it if they
  // want to be reached on a different phone (e.g. a family member's).
  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, contact_mobile: f.contact_mobile || loginMobile || "" }));
    }
  }, [open, loginMobile]);

  const reset = () =>
    setForm({
      subject: "",
      category: "Other",
      priority: "medium",
      order_id: "",
      contact_mobile: loginMobile || "",
      complaints: [emptyComplaint()],
    });

  const updateComplaint = (idx, patch) => {
    setForm((f) => ({
      ...f,
      complaints: f.complaints.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  };

  const addComplaint = () => {
    setForm((f) => {
      if (f.complaints.length >= MAX_COMPLAINTS) {
        toast.error(`Up to ${MAX_COMPLAINTS} complaints per ticket.`);
        return f;
      }
      return { ...f, complaints: [...f.complaints, emptyComplaint()] };
    });
  };

  const removeComplaint = (idx) => {
    setForm((f) => {
      if (f.complaints.length <= 1) return f; // keep at least one
      return { ...f, complaints: f.complaints.filter((_, i) => i !== idx) };
    });
  };

  const onAttachForComplaint = async (idx, e) => {
    // Multi-file picker — up to 3 photos per complaint.
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking the same file(s)
    if (!files.length) return;
    const currentPhotos = form.complaints[idx]?.attachments || [];
    const room = MAX_PHOTOS_PER_COMPLAINT - currentPhotos.length;
    if (room <= 0) {
      toast.error(`Up to ${MAX_PHOTOS_PER_COMPLAINT} photos per complaint.`);
      return;
    }
    const picked = files.slice(0, room);
    if (files.length > room) {
      toast.warning(`Only ${room} more photo${room === 1 ? "" : "s"} allowed — extras were skipped.`);
    }
    const newOnes = [];
    for (const file of picked) {
      if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
        toast.error(`Skipped ${file.name}: only JPEG, PNG or WebP allowed.`);
        continue;
      }
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`Skipped ${file.name}: image is over 15 MB.`);
        continue;
      }
      try {
        const r = await compressImage(file);
        newOnes.push(r.dataUrl);
      } catch {
        toast.error(`Could not read ${file.name}.`);
      }
    }
    if (newOnes.length) {
      updateComplaint(idx, {
        attachments: [...currentPhotos, ...newOnes].slice(0, MAX_PHOTOS_PER_COMPLAINT),
      });
    }
  };

  const removeAttachment = (idx, photoIdx) => {
    const photos = form.complaints[idx]?.attachments || [];
    updateComplaint(idx, {
      attachments: photos.filter((_, i) => i !== photoIdx),
    });
  };

  const submit = async () => {
    const subject = form.subject.trim();
    if (subject.length < 5) {
      toast.error("Please describe your issue in the subject (5+ characters).");
      return;
    }
    const contact = String(form.contact_mobile || "").replace(/[^\d+]/g, "");
    if (contact.replace(/^\+/, "").length < 10) {
      toast.error("Please share a valid mobile number so we can reach you.");
      return;
    }
    // Collect only the complaints the customer actually filled in.
    const cleaned = form.complaints
      .map((c) => ({
        description: (c.description || "").trim(),
        attachments: c.attachments || [],
      }))
      .filter((c) => c.description.length > 0);
    if (cleaned.length === 0) {
      toast.error("Please describe at least one complaint.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        subject,
        category: form.category,
        priority: form.priority,
        // Legacy `description` is set to the first complaint so older admin
        // consoles that don't yet render `ticket_complaints` still see it.
        description: cleaned[0].description,
        contact_mobile: contact,
        complaints: cleaned,
      };
      if (form.order_id) payload.order_id = form.order_id;
      const { data } = await api.post("/store/tickets", payload);
      toast.success("Ticket raised — the shop will get back to you soon.");
      reset();
      onOpenChange(false);
      if (typeof onCreated === "function") onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not raise the ticket. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-[var(--shop-primary,#0055FF)]" /> Raise a service ticket
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Describe your issue and the shop owner will respond as a task in their dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="e.g. Order arrived damaged"
              maxLength={120}
              data-testid="ticket-subject"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-slate-500" />
              Contact number{" "}
              <span className="text-slate-400 text-xs">
                (our team will call you back on this number)
              </span>
            </Label>
            <div className="flex gap-2">
              <Input
                type="tel"
                inputMode="tel"
                value={form.contact_mobile}
                onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })}
                placeholder="+91 98765 43210"
                data-testid="ticket-contact-mobile"
              />
              {loginMobile && form.contact_mobile !== loginMobile && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, contact_mobile: loginMobile })}
                  className="mono text-[11px] px-2 rounded border border-slate-200 text-slate-500 hover:text-[var(--shop-primary,#0055FF)] hover:border-[var(--shop-primary,#0055FF)] whitespace-nowrap"
                  data-testid="ticket-contact-use-login"
                  title="Use the number you signed up with"
                >
                  Use login #
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white"
                data-testid="ticket-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <div className="flex gap-1">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setForm({ ...form, priority: p.key })}
                    className={
                      "flex-1 h-10 rounded-md text-xs font-medium border transition " +
                      (form.priority === p.key
                        ? p.color + " border-current"
                        : "text-slate-500 border-slate-200 hover:bg-slate-50")
                    }
                    data-testid={`ticket-priority-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {orders.length > 0 && (
            <div className="space-y-1.5">
              <Label>Link to an order <span className="text-slate-400 text-xs">(optional)</span></Label>
              <select
                value={form.order_id}
                onChange={(e) => setForm({ ...form, order_id: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white"
                data-testid="ticket-order"
              >
                <option value="">— None —</option>
                {orders.slice(0, 20).map((o) => (
                  <option key={o.id} value={o.id}>
                    #{o.id.slice(0, 8)} · ₹{Number(o.total || 0).toLocaleString("en-IN")} · {o.status}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2" data-testid="ticket-complaints">
            <div className="flex items-center justify-between">
              <Label>What happened?</Label>
              <span className="mono text-[10px] text-slate-500">
                {form.complaints.length} / {MAX_COMPLAINTS} complaint{form.complaints.length === 1 ? "" : "s"}
              </span>
            </div>

            {form.complaints.map((c, idx) => (
              <div
                key={c.key}
                className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-2"
                data-testid={`ticket-complaint-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span className="mono text-[10px] uppercase tracking-widest text-slate-500">
                    Complaint {idx + 1}{idx === 0 ? " · primary" : ""}
                  </span>
                  {form.complaints.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeComplaint(idx)}
                      className="text-slate-400 hover:text-red-600 mono text-[10px] uppercase tracking-widest flex items-center gap-1"
                      data-testid={`ticket-complaint-remove-${idx}`}
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                  )}
                </div>
                <Textarea
                  rows={idx === 0 ? 3 : 2}
                  value={c.description}
                  onChange={(e) => updateComplaint(idx, { description: e.target.value })}
                  placeholder={
                    idx === 0
                      ? "Add any detail that would help — order id, timings, symptoms."
                      : "Describe the additional issue in one line."
                  }
                  className="bg-white"
                  data-testid={`ticket-complaint-desc-${idx}`}
                />

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 mono text-[10px] text-slate-500">
                    <ImagePlus className="w-3 h-3" />
                    Photos ({c.attachments.length}/{MAX_PHOTOS_PER_COMPLAINT})
                  </div>
                  <div className="grid grid-cols-3 gap-2" data-testid={`ticket-complaint-photos-${idx}`}>
                    {c.attachments.map((src, pIdx) => (
                      <div key={pIdx} className="relative aspect-square">
                        <img
                          src={src}
                          alt={`Complaint ${idx + 1} photo ${pIdx + 1}`}
                          className="w-full h-full object-cover rounded-md border border-slate-200 bg-white"
                          data-testid={`ticket-complaint-photo-preview-${idx}-${pIdx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx, pIdx)}
                          className="absolute top-1 right-1 bg-white/95 border border-slate-200 rounded-full p-1 hover:bg-red-50 hover:text-red-600 shadow-sm"
                          data-testid={`ticket-complaint-photo-remove-${idx}-${pIdx}`}
                          title="Remove photo"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {c.attachments.length < MAX_PHOTOS_PER_COMPLAINT && (
                      <>
                        <label
                          className="aspect-square flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-200 hover:border-[var(--shop-primary,#0055FF)] rounded-md cursor-pointer transition text-xs text-slate-500 hover:text-[var(--shop-primary,#0055FF)] bg-white"
                          data-testid={`ticket-complaint-photo-label-${idx}`}
                        >
                          <ImagePlus className="w-4 h-4" />
                          <span>Photo</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            className="hidden"
                            onChange={(e) => onAttachForComplaint(idx, e)}
                            data-testid={`ticket-complaint-photo-input-${idx}`}
                          />
                        </label>
                        {c.attachments.length < MAX_PHOTOS_PER_COMPLAINT - 1 && (
                          <label
                            className="aspect-square flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-200 hover:border-[var(--shop-primary,#0055FF)] rounded-md cursor-pointer transition text-xs text-slate-500 hover:text-[var(--shop-primary,#0055FF)] bg-white"
                            data-testid={`ticket-complaint-photo-camera-label-${idx}`}
                          >
                            <Camera className="w-4 h-4" />
                            <span>Camera</span>
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              onChange={(e) => onAttachForComplaint(idx, e)}
                              data-testid={`ticket-complaint-photo-camera-${idx}`}
                            />
                          </label>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {form.complaints.length < MAX_COMPLAINTS && (
              <button
                type="button"
                onClick={addComplaint}
                className="w-full h-10 rounded-md border-2 border-dashed border-slate-200 hover:border-[var(--shop-primary,#0055FF)] text-sm text-slate-600 hover:text-[var(--shop-primary,#0055FF)] transition flex items-center justify-center gap-1.5"
                data-testid="ticket-complaint-add"
              >
                <Plus className="w-4 h-4" /> Add another complaint
              </button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)]"
            data-testid="ticket-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Raise ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Floating help/support button — pins to bottom-right on every /shop page.
 *
 * Historically the FAB was hidden until the customer's session was confirmed
 * (`if (!authed) return null`). Two problems on the field:
 *   1. Older Android phones ran the `/store/me` probe against stale cookies
 *      and never resolved, leaving `authed` stuck at `null`; the FAB never
 *      appeared even for logged-in shoppers.
 *   2. Guest visitors browsing the storefront couldn't see a way to raise a
 *      ticket at all, which defeated the whole purpose of the button.
 * The FAB is now ALWAYS rendered on shop pages. Guests tapping it are
 * routed to /shop/login with a `next=` return URL so they land right back
 * where they were.
 */
export function SupportFAB() {
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loginMobile, setLoginMobile] = useState("");
  const [authed, setAuthed] = useState(null); // null=unknown, true/false=known
  const location = useLocation();
  const navigate = useNavigate();

  // Re-probe the customer session whenever the pathname changes. We no
  // longer *depend* on this to render the button — it just lets us skip
  // the login redirect when the shopper is already signed in.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/store/me")
      .then(() => {
        if (!cancelled) setAuthed(true);
      })
      .catch(() => {
        if (!cancelled) setAuthed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const openDialog = useCallback(async () => {
    // If we're sure the shopper isn't logged in yet, send them to the
    // customer login page with a return URL so they land back on the same
    // screen after signing in. If auth is still unknown (probe pending) we
    // optimistically try to open the dialog — the API call inside will
    // return 401 and the axios interceptor will handle it.
    if (authed === false) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/shop/login?next=${next}`);
      return;
    }
    // Fetch recent orders + login mobile once, on open, so the dialog has
    // everything it needs without a blocking wait.
    try {
      const [ord, me] = await Promise.all([
        api.get("/store/orders").catch(() => ({ data: [] })),
        api.get("/store/me").catch(() => ({ data: {} })),
      ]);
      setOrders(ord.data || []);
      setLoginMobile(me.data?.mobile || "");
    } catch {
      /* ignore */
    }
    setOpen(true);
  }, [authed, location.pathname, location.search, navigate]);

  // On the Team Chat page the send button already sits in this corner —
  // showing the FAB there makes the two nearly impossible to tell apart on mobile.
  const onChatPage = location.pathname.startsWith("/shop/chat");

  return (
    <>
      {!onChatPage && (
        <button
          onClick={openDialog}
          className="fixed bottom-5 right-5 z-40 bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)] text-white rounded-full shadow-lg h-12 pl-4 pr-5 flex items-center gap-2 text-sm font-medium transition-transform hover:scale-105"
          data-testid="support-fab"
          title="Need help? Raise a ticket"
        >
          <HelpCircle className="w-5 h-5" />
          Help
        </button>
      )}
      <TicketDialog open={open} onOpenChange={setOpen} orders={orders} loginMobile={loginMobile} />
    </>
  );
}

/** Ticket list — used by the /shop/account page and /shop/tickets. */
export function TicketList({ tickets, loading }) {
  if (loading) {
    return (
      <div className="text-center py-6 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }
  if (!tickets || tickets.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Ticket className="w-8 h-8 mx-auto mb-2 opacity-60" />
        <div className="text-sm">No tickets yet.</div>
        <div className="mono text-[11px] mt-1">Tap the Help button anytime to raise one.</div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="ticket-list">
      {tickets.map((t) => {
        const meta = STATUS_META[t.status] || STATUS_META.pending;
        return (
          <div
            key={t.id}
            className="border border-slate-200 rounded-md p-3 bg-white"
            data-testid={`ticket-row-${t.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium line-clamp-1">
                  {t.ticket_subject || t.title}
                </div>
                <div className="mono text-[10px] text-slate-500 mt-0.5">
                  {(t.ticket_category || "Other")} · {new Date(t.created_at).toLocaleString("en-IN", {
                    dateStyle: "medium", timeStyle: "short",
                  })}
                </div>
              </div>
              <span className={"mono text-[10px] px-1.5 py-0.5 rounded uppercase " + meta.color}>
                {meta.label}
              </span>
            </div>
            {t.description && !(Array.isArray(t.ticket_complaints) && t.ticket_complaints.length > 1) && (
              <p className="text-xs text-slate-600 mt-2 line-clamp-2">{t.description}</p>
            )}
            {(() => {
              // Prefer the multi-complaint array; fall back to the legacy
              // single description + shared photos for older tickets.
              const complaints = Array.isArray(t.ticket_complaints) && t.ticket_complaints.length
                ? t.ticket_complaints
                : null;
              if (complaints) {
                const doneCount = complaints.filter((c) => c.status === "done").length;
                return (
                  <div className="mt-2 space-y-2" data-testid={`ticket-complaints-${t.id}`}>
                    {complaints.length > 1 && (
                      <div className="mono text-[10px] text-slate-500">
                        {doneCount}/{complaints.length} complaints resolved
                      </div>
                    )}
                    {complaints.map((c, idx) => (
                      <div
                        key={c.id || idx}
                        className={
                          "rounded border p-2 text-xs " +
                          (c.status === "done"
                            ? "border-emerald-200 bg-emerald-50/40"
                            : "border-slate-200 bg-slate-50/40")
                        }
                        data-testid={`ticket-complaint-row-${t.id}-${idx}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span
                            className={
                              "flex-1 " +
                              (c.status === "done" ? "line-through text-slate-500" : "text-slate-700")
                            }
                          >
                            {complaints.length > 1 && (
                              <b className="mono text-[10px] text-slate-500 mr-1">#{idx + 1}</b>
                            )}
                            {c.description}
                          </span>
                          {c.status === "done" && (
                            <span className="mono text-[10px] uppercase text-emerald-700 shrink-0">
                              Resolved
                            </span>
                          )}
                        </div>
                        {c.attachments && c.attachments.length > 0 && (
                          <div className="mt-1.5 grid grid-cols-3 gap-1">
                            {c.attachments.map((src, pIdx) => (
                              <img
                                key={pIdx}
                                src={src}
                                alt={`Complaint ${idx + 1} photo ${pIdx + 1}`}
                                className="aspect-square rounded border border-slate-200 object-cover bg-white"
                                data-testid={`ticket-complaint-thumb-${t.id}-${idx}-${pIdx}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              }
              // Legacy single-attachment fallback
              const imgs = (t.ticket_attachments && t.ticket_attachments.length)
                ? t.ticket_attachments
                : (t.ticket_attachment ? [t.ticket_attachment] : []);
              if (!imgs.length) return null;
              return (
                <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid={`ticket-attachments-${t.id}`}>
                  {imgs.map((src, idx) => (
                    <img
                      key={idx}
                      src={src}
                      alt={`Ticket attachment ${idx + 1}`}
                      className="aspect-square rounded border border-slate-200 object-cover"
                      data-testid={`ticket-attachment-thumb-${t.id}-${idx}`}
                    />
                  ))}
                </div>
              );
            })()}
            {t.status === "done" && t.done_comment && (
              <div className="mt-2 border-l-2 border-emerald-300 pl-2 text-xs text-emerald-800 bg-emerald-50/50 py-1.5">
                <b>Shop:</b> {t.done_comment}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
